import { useCallback, useEffect, useState } from "react";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { Accordion, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { LoadingLogo } from "../../../components/Modal";

import { 
  GetCurrentUserMarathonEventQuery,
  GetCurrentUserMarathonEventQueryVariables,
  GetUserMarathonAchievementsQuery,
  GetUserMarathonAchievementsQueryVariables,
  MissionTaskType,
  ResetUserMarathonEventMutation,
  ResetUserMarathonEventMutationVariables,
  UpdateUserMarathonAchievementProgressMutation,
  UpdateUserMarathonAchievementProgressMutationVariables,
  UpdateUserMarathonEventMutation,
  UpdateUserMarathonEventMutationVariables,
  UserSearchType
} from "../../../../../../__gqltypes__";
import { GET_CURRENT_USER_MARATHON_EVENT, GET_USER_MARATHON_ACHIEVEMENTS, RESET_USER_MARATHON_EVENT, UPDATE_USER_MARATHON_ACHIEVEMENTS, UPDATE_USER_MARATHON_EVENT } from "../../../graphql";

type Props = {
  userId: string;
}

export function UserMarathonEventDisplay({userId}: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const [useCheckPoint, setUseCheckPoint] = useState<undefined | boolean>(undefined);
  const [newMaxReached, setNewMaxReached] = useState(0);
  const [isRefetching, setIsRefetching] = useState(false);

  const [aProgress, setAProgress] = useState(0);

  const {data: uemData, loading: dataLoading, refetch} = useQuery<
    GetCurrentUserMarathonEventQuery, 
    GetCurrentUserMarathonEventQueryVariables>(GET_CURRENT_USER_MARATHON_EVENT, {
      variables: {
        property: userId,
        type: UserSearchType.BY_ID
      },
      skip: !userId,
    });

  const {data: uma, loading: umaLoading} = useQuery<
    GetUserMarathonAchievementsQuery,
    GetUserMarathonAchievementsQueryVariables
  >(GET_USER_MARATHON_ACHIEVEMENTS, {
    variables: {
      property: userId,
      type: UserSearchType.BY_ID,
    },
    skip: !userId,
  });

  const onRefetch = useCallback(() => {
    setIsRefetching(true);
    refetch().finally(() => {
      setIsRefetching(false);
    });
  }, [refetch]);

  const onCompleted = useCallback(() => {
    onRefetch();
  }, [onRefetch]);
  const uem = dataLoading ? null : uemData?.user?.userMarathonEvent;
  const [updateUME, {loading: updateLoading}] = useMutation<
    UpdateUserMarathonEventMutation,
    UpdateUserMarathonEventMutationVariables>(
      UPDATE_USER_MARATHON_EVENT, {
        onCompleted,
      }
    );

  const [resetUME, {loading: resetLoading}] = useMutation<
    ResetUserMarathonEventMutation,
    ResetUserMarathonEventMutationVariables>(
      RESET_USER_MARATHON_EVENT, {
        onCompleted,
      }
    );

  const [updateUMEAchievement, {loading: updateALoading}] = useMutation<
    UpdateUserMarathonAchievementProgressMutation,
    UpdateUserMarathonAchievementProgressMutationVariables>(UPDATE_USER_MARATHON_ACHIEVEMENTS);

  useEffect(() => {
    if (uem) {
      setNewMaxReached(uem.maxSongReached);
      setUseCheckPoint(uem.checkPoint > 0);
    }
  }, [uem]);

  useEffect(() => {
    if (uma) {
      const missions = uma.user?.achievementMissions?.missions;
      if (!missions) {
        return;
      }
      let maxProgress = 0;
      missions.forEach((val) => {
        if(val.mission.tasks[0] && val.mission.tasks[0].type === MissionTaskType.COMPLETE_MARATHON) {
          maxProgress = Math.max(maxProgress, val.progress);
        }
      });
      setAProgress(maxProgress);
    }
  }, [uma]);
  
  const handleResetRewards = useCallback(() => {
    if (!uem) {
      return;
    }
     updateUME({
      variables: {
        input: {
          userId,
          eventId: uem.eventId,
          resetClaimedRewards: true,
        },
      },
    });
  }, [uem, updateUME, userId]);

  const handleUpdateAchievementsProgress = useCallback((newProgress = 0) => {
    updateUMEAchievement({variables: {input: {
      userId,
      newProgress,
    }}});
  }, [updateUMEAchievement, userId]);

  const handleResetLastRound = useCallback(() => {
    if (!uem) {
      return;
    }
    resetUME({
      variables: {
        input: {
          userId,
          eventId: uem.eventId,
          isFull: false,
          isLastRound: true,
        }
      }
    });
  }, [uem, resetUME, userId]);

  const handleFullReset = useCallback(() => {
    if (!uem) {
      return;
    }
    resetUME({
      variables: {
        input: {
          userId,
          eventId: uem.eventId,
          isFull: true,
          isLastRound: false,
        }
      }
    });
  }, [uem, resetUME, userId]);

  const handleSave = useCallback(() => {
    if (!uem) {
      return;
    }
    
    updateUME({
      variables: {
        input: {
          userId,
          eventId: uem.eventId,
          useCheckPoint,
          newMaxReached
        },
      },
    });
  }, [newMaxReached, uem, updateUME, useCheckPoint, userId]);

  const loading = dataLoading || updateLoading || resetLoading || isRefetching || updateALoading || updateALoading;
  return (
    <Card className="mb-4">
      <Card.Body>
        <Accordion>
           <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(value => !value)}>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              User Marathon Event
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            {loading ? 
              (
                <LoadingLogo show={loading} />
              ) : !uem ? 
              (
                <Row className="mb-3">
                  <Col md={4}>User is not participating in marathon right now.</Col>
                </Row>
              ) : 
              (
                <>
                  <Row className="mb-3">
                    <Col md="auto">
                      <Button  className='align-self-center mr-2' onClick={onRefetch}>
                        <i className="fas fa-redo"/> Refresh
                      </Button>
                      <Button className='align-self-center mr-2' onClick={handleSave}>
                        <i className="fas fa-save"/> Save
                      </Button>
                      <Button className='align-self-center mr-2' onClick={handleResetRewards}>
                        <i className="fas fa-box-open"/> Reset claimed rewards
                      </Button>
                      <Button variant="danger" className='align-self-center mr-2' onClick={handleResetLastRound}>
                        <i className="fas fa-trash"/> Reset last round
                      </Button>
                      <Button variant="danger" className='align-self-center mr-2' onClick={handleFullReset}>
                        <i className="fas fa-battery-empty"/> Full reset
                      </Button>
                      <Button className='align-self-center mr-2' onClick={() => handleUpdateAchievementsProgress(aProgress)}>
                        <i className="fas fa-save"/> Update achievements
                      </Button>
                      <Button className='align-self-center mr-2' onClick={handleUpdateAchievementsProgress}>
                        <i className="fas fa-box-open"/> Reset achievements
                      </Button>
                    </Col>
                  </Row> 
                  <Row className="mb-3">
                    <Col md='auto'>New progress:</Col>
                    <Form.Group as={Col} xs="auto" className="mr-4">
                      <Form.Control 
                        type="text"
                        value={newMaxReached}
                        onChange={(event) => {
                          const number = !Number.isNaN(+event.target.value) ? +event.target.value : 0;
                          setNewMaxReached(Math.min(100, number));
                        }} />
                    </Form.Group>
                    <Col md='auto'>New achievement progress:</Col>
                    <Form.Group as={Col} xs="auto">
                      <Form.Control 
                        type="text"
                        value={aProgress}
                        onChange={(event) => {
                          const number = !Number.isNaN(+event.target.value) ? +event.target.value : 0;
                          setAProgress(Math.max(0, Math.min(20, number)));
                        }} />
                    </Form.Group>
                  </Row>
                  <Row className="mb-3">
                    <Col md="auto">Use check point?</Col>
                    <Form.Check type="checkbox" checked={useCheckPoint ?? false} onChange={(event) => {
                      setUseCheckPoint(prev => !prev);
                    }}/>
                  </Row>
                  <Card style={{width: '60%', marginBottom: 10}}>
                    <Card.Header className="text-center">
                      <Card.Title>Claimed rewards</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <Table bordered responsive hover size="sm">
                        <thead className="text-center">
                        <tr>
                          <th>Guesses</th>
                          <th>Basic reward</th>
                          <th>Plus reward</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uem?.milestoneClaimed.map((milestoneInfo, index) => {
                          return (
                            <tr key={index}>
                              <td>
                                <div className="d-flex justify-content-center">
                                  {(milestoneInfo.index + 1)* 10}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex justify-content-center">
                                  {milestoneInfo.isBasicClaimed ? 
                                    (<i className="fas fa-check-square" style={{color: '#2bf702'}}/>) : 
                                    (<i className="fas fa-times-circle" style={{color: '#FF0000'}}/>)}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex justify-content-center">
                                   {milestoneInfo.isPlusClaimed ? 
                                    (<i className="fas fa-check-square" style={{color: '#2bf702'}}/>) : 
                                    (<i className="fas fa-times-circle" style={{color: '#FF0000'}}/>)}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                  
                </>
              )} 
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>);
}